import React, { useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { useFetchEmployeesQuery } from "../../store/api/employeesApi";
import { useFetchClientsQuery } from "../../store/api/clientsApi";
import {
  useChangeAdminPasswordMutation,
  useResetEmployeePasswordMutation,
  useResetClientPasswordMutation,
} from "../../store/api/passwordApi";
import "./admin.css";

// ─── Helpers ─────────────────────────────────────────────────────────────────

type TabId = "mine" | "employee" | "client";

const TABS: {
  id: TabId;
  label: string;
  icon: string;
  accentColor: string;
  borderColor: string;
}[] = [
  {
    id: "mine",
    label: "My Password",
    icon: "bi-key-fill",
    accentColor: "#0d9488",
    borderColor: "#14b8a6",
  },
  {
    id: "employee",
    label: "Employee Password",
    icon: "bi-person-badge-fill",
    accentColor: "#1d4ed8",
    borderColor: "#3b82f6",
  },
  {
    id: "client",
    label: "Client Password",
    icon: "bi-people-fill",
    accentColor: "#7c3aed",
    borderColor: "#a855f7",
  },
];

const generatePassword = (): string => {
  const charset =
    "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$";
  return Array.from(
    { length: 12 },
    () => charset[Math.floor(Math.random() * charset.length)],
  ).join("");
};

// ─── Shared password field ────────────────────────────────────────────────────

interface PwFieldProps {
  id: string;
  label: string;
  subtitle?: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
  borderColor: string;
  bgColor: string;
  iconColor: string;
  icon: string;
  onGenerate?: () => void;
}

const PwField: React.FC<PwFieldProps> = ({
  id,
  label,
  subtitle,
  value,
  onChange,
  show,
  onToggleShow,
  borderColor,
  bgColor,
  iconColor,
  icon,
  onGenerate,
}) => (
  <div style={{ borderLeft: `3px solid ${borderColor}`, paddingLeft: 10 }}>
    <label
      htmlFor={id}
      className='form-label mb-1 fw-semibold'
      style={{ fontSize: "0.84rem", color: "#334155" }}
    >
      {label} <span className='text-danger'>*</span>
      {subtitle && (
        <small
          className='d-block fw-normal text-muted'
          style={{ fontSize: "0.71rem" }}
        >
          {subtitle}
        </small>
      )}
    </label>
    <div className='input-group input-group-sm'>
      <span
        className='input-group-text'
        style={{ background: bgColor, borderColor, color: iconColor }}
      >
        <i className={`bi ${icon}`} style={{ fontSize: 12 }} />
      </span>
      <input
        id={id}
        type={show ? "text" : "password"}
        className='form-control form-control-sm'
        style={{ borderColor, boxShadow: "none" }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete='new-password'
      />
      <button
        type='button'
        className='btn btn-outline-secondary btn-sm'
        onClick={onToggleShow}
        title={show ? "Hide" : "Show"}
      >
        <i className={`bi ${show ? "bi-eye-slash" : "bi-eye"}`} />
      </button>
      {onGenerate && (
        <button
          type='button'
          className='btn btn-outline-secondary btn-sm'
          onClick={onGenerate}
          title='Generate strong password'
        >
          <i className='bi bi-arrow-repeat' />
        </button>
      )}
    </div>
  </div>
);

// ─── Tab 1: My Password ───────────────────────────────────────────────────────

const MyPasswordTab: React.FC = () => {
  const { adminUser } = useAdminAuth();
  const [changePassword, { isLoading }] = useChangeAdminPasswordMutation();

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetForm = () => {
    setCurrentPw("");
    setNewPw("");
    setConfirmPw("");
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!currentPw) {
      setError("Current password is required.");
      return;
    }
    if (!newPw) {
      setError("New password is required.");
      return;
    }
    if (newPw.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("New passwords do not match.");
      return;
    }
    if (currentPw === newPw) {
      setError("New password must differ from the current password.");
      return;
    }
    try {
      await changePassword({
        currentPassword: currentPw,
        newPassword: newPw,
      }).unwrap();
      setSuccess("Password changed successfully.");
      resetForm();
      window.setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const initial = adminUser?.name?.trim().charAt(0).toUpperCase() || "A";

  return (
    <div>
      {adminUser && (
        <div
          className='d-flex align-items-center gap-3 mb-4 p-3 rounded-3'
          style={{ background: "#f0fdfa", border: "1px solid #99f6e4" }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #0d9488, #14b8a6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 800,
              fontSize: "1.1rem",
              flexShrink: 0,
            }}
          >
            {initial}
          </div>
          <div>
            <div className='fw-semibold' style={{ color: "#0f766e" }}>
              {adminUser.name}
            </div>
            <div className='small text-muted'>
              {adminUser.email} &bull; Admin
            </div>
          </div>
        </div>
      )}

      {error && <div className='alert alert-danger py-2 mb-3'>{error}</div>}
      {success && (
        <div className='alert alert-success d-flex align-items-center gap-2 py-2 mb-3'>
          <i className='bi bi-check-circle-fill' /> {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className='row g-3'>
          <div className='col-12'>
            <PwField
              id='adminCurrentPw'
              label='Current Password'
              subtitle='Enter your existing admin password to verify identity'
              value={currentPw}
              onChange={setCurrentPw}
              show={showCurrent}
              onToggleShow={() => setShowCurrent((v) => !v)}
              borderColor='#6366f1'
              bgColor='#eef2ff'
              iconColor='#6366f1'
              icon='bi-lock-fill'
            />
          </div>
          <div className='col-md-6'>
            <PwField
              id='adminNewPw'
              label='New Password'
              subtitle='Min. 8 characters — click ↻ to auto-generate'
              value={newPw}
              onChange={setNewPw}
              show={showNew}
              onToggleShow={() => setShowNew((v) => !v)}
              borderColor='#0d9488'
              bgColor='#ccfbf1'
              iconColor='#0f766e'
              icon='bi-shield-lock-fill'
              onGenerate={() => {
                const p = generatePassword();
                setNewPw(p);
                setConfirmPw(p);
              }}
            />
          </div>
          <div className='col-md-6'>
            <PwField
              subtitle='Min. 8 characters — and should match the new password'
              id='adminConfirmPw'
              label='Confirm New Password'
              value={confirmPw}
              onChange={setConfirmPw}
              show={showConfirm}
              onToggleShow={() => setShowConfirm((v) => !v)}
              borderColor='#0d9488'
              bgColor='#ccfbf1'
              iconColor='#0f766e'
              icon='bi-shield-check'
            />
          </div>
          <div className='col-12'>
            <div style={{ borderTop: "1.5px dashed #e2e8f0", paddingTop: 14 }}>
              <button
                type='submit'
                disabled={isLoading}
                style={{
                  background: "linear-gradient(135deg, #0d9488, #14b8a6)",
                  border: "none",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "7px 20px",
                  fontSize: "0.83rem",
                  fontWeight: 700,
                  cursor: isLoading ? "wait" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  boxShadow: "0 3px 10px #0d948833",
                }}
              >
                {isLoading ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    />{" "}
                    Saving…
                  </>
                ) : (
                  <>
                    <i className='bi bi-check2-circle' /> Change Password
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// ─── Tab 2: Employee Password Reset ──────────────────────────────────────────

const EmployeePasswordTab: React.FC = () => {
  const { data: employees = [], isLoading: loadingEmployees } =
    useFetchEmployeesQuery();
  const [resetPassword, { isLoading }] = useResetEmployeePasswordMutation();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedEmployee = employees.find((e) => e.id === selectedId);

  const resetForm = () => {
    setNewPw("");
    setConfirmPw("");
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (selectedId == null) {
      setError("Please select an employee.");
      return;
    }
    if (!newPw) {
      setError("New password is required.");
      return;
    }
    if (newPw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("Passwords do not match.");
      return;
    }
    try {
      await resetPassword({
        id: selectedId,
        newPassword: newPw,
      }).unwrap();
      setSuccess(
        `Password reset for ${selectedEmployee?.firstName} ${selectedEmployee?.lastName}.`,
      );
      resetForm();
      window.setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  return (
    <div>
      {error && <div className='alert alert-danger py-2 mb-3'>{error}</div>}
      {success && (
        <div className='alert alert-success d-flex align-items-center gap-2 py-2 mb-3'>
          <i className='bi bi-check-circle-fill' /> {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className='row g-3'>
          <div className='col-12'>
            <div style={{ borderLeft: "3px solid #3b82f6", paddingLeft: 10 }}>
              <label
                htmlFor='empSelect'
                className='form-label mb-1 fw-semibold'
                style={{ fontSize: "0.84rem", color: "#334155" }}
              >
                Select Employee <span className='text-danger'>*</span>
                <small
                  className='d-block fw-normal text-muted'
                  style={{ fontSize: "0.71rem" }}
                >
                  Choose the employee whose password you want to reset
                </small>
              </label>
              <div className='input-group input-group-sm'>
                <span
                  className='input-group-text'
                  style={{
                    background: "#dbeafe",
                    borderColor: "#93c5fd",
                    color: "#1d4ed8",
                  }}
                >
                  <i
                    className='bi bi-person-badge-fill'
                    style={{ fontSize: 12 }}
                  />
                </span>
                <select
                  id='empSelect'
                  className='form-select form-select-sm'
                  style={{ borderColor: "#93c5fd", boxShadow: "none" }}
                  value={selectedId ?? ""}
                  onChange={(e) => {
                    setSelectedId(e.target.value ? Number(e.target.value) : null);
                    resetForm();
                  }}
                  disabled={loadingEmployees}
                  aria-label='Select employee'
                >
                  <option value=''>
                    {loadingEmployees
                      ? "Loading employees…"
                      : "— Select employee —"}
                  </option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={String(emp.id)}>
                      {emp.firstName} {emp.lastName} — {emp.department} (
                      {emp.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {selectedId != null && (
            <>
              <div className='col-12'>
                <div
                  className='d-flex align-items-center gap-3 p-3 rounded-3'
                  style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "1rem",
                      flexShrink: 0,
                    }}
                  >
                    {selectedEmployee?.firstName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className='fw-semibold' style={{ color: "#1e40af" }}>
                      {selectedEmployee?.firstName} {selectedEmployee?.lastName}
                    </div>
                    <div className='small text-muted'>
                      {selectedEmployee?.email} &bull;{" "}
                      {selectedEmployee?.department}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <PwField
                  id='empNewPw'
                  label='New Password'
                  subtitle='Min. 8 characters — click ↻ to auto-generate'
                  value={newPw}
                  onChange={setNewPw}
                  show={showNew}
                  onToggleShow={() => setShowNew((v) => !v)}
                  borderColor='#3b82f6'
                  bgColor='#dbeafe'
                  iconColor='#1d4ed8'
                  icon='bi-shield-lock-fill'
                  onGenerate={() => {
                    const p = generatePassword();
                    setNewPw(p);
                    setConfirmPw(p);
                  }}
                />
              </div>
              <div className='col-md-6'>
                <PwField
                  id='empConfirmPw'
                  subtitle='Min. 8 characters — and should match the new password'
                  label='Confirm Password'
                  value={confirmPw}
                  onChange={setConfirmPw}
                  show={showConfirm}
                  onToggleShow={() => setShowConfirm((v) => !v)}
                  borderColor='#3b82f6'
                  bgColor='#dbeafe'
                  iconColor='#1d4ed8'
                  icon='bi-shield-check'
                />
              </div>
              <div className='col-12'>
                <div
                  style={{ borderTop: "1.5px dashed #e2e8f0", paddingTop: 14 }}
                >
                  <button
                    type='submit'
                    disabled={isLoading}
                    style={{
                      background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                      border: "none",
                      color: "#fff",
                      borderRadius: 8,
                      padding: "7px 20px",
                      fontSize: "0.83rem",
                      fontWeight: 700,
                      cursor: isLoading ? "wait" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      boxShadow: "0 3px 10px #1d4ed833",
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        />{" "}
                        Resetting…
                      </>
                    ) : (
                      <>
                        <i className='bi bi-shield-check' /> Reset Employee
                        Password
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

// ─── Tab 3: Client Password Reset ────────────────────────────────────────────

const ClientPasswordTab: React.FC = () => {
  const { data: clients = [], isLoading: loadingClients } =
    useFetchClientsQuery();
  const [resetPassword, { isLoading }] = useResetClientPasswordMutation();

  const [selectedId, setSelectedId] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedClient = clients.find((c) => c.id === selectedId);

  const resetForm = () => {
    setNewPw("");
    setConfirmPw("");
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!selectedId) {
      setError("Please select a client.");
      return;
    }
    if (!newPw) {
      setError("New password is required.");
      return;
    }
    if (newPw.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setError("Passwords do not match.");
      return;
    }
    try {
      await resetPassword({ id: selectedId, newPassword: newPw }).unwrap();
      setSuccess(`Password reset for client ${selectedClient?.name}.`);
      resetForm();
      window.setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  return (
    <div>
      {error && <div className='alert alert-danger py-2 mb-3'>{error}</div>}
      {success && (
        <div className='alert alert-success d-flex align-items-center gap-2 py-2 mb-3'>
          <i className='bi bi-check-circle-fill' /> {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className='row g-3'>
          <div className='col-12'>
            <div style={{ borderLeft: "3px solid #a855f7", paddingLeft: 10 }}>
              <label
                htmlFor='clientSelect'
                className='form-label mb-1 fw-semibold'
                style={{ fontSize: "0.84rem", color: "#334155" }}
              >
                Select Client <span className='text-danger'>*</span>
                <small
                  className='d-block fw-normal text-muted'
                  style={{ fontSize: "0.71rem" }}
                >
                  Choose the client whose portal password you want to reset
                </small>
              </label>
              <div className='input-group input-group-sm'>
                <span
                  className='input-group-text'
                  style={{
                    background: "#f3e8ff",
                    borderColor: "#d8b4fe",
                    color: "#7c3aed",
                  }}
                >
                  <i className='bi bi-people-fill' style={{ fontSize: 12 }} />
                </span>
                <select
                  id='clientSelect'
                  className='form-select form-select-sm'
                  style={{ borderColor: "#d8b4fe", boxShadow: "none" }}
                  value={selectedId}
                  onChange={(e) => {
                    setSelectedId(e.target.value);
                    resetForm();
                  }}
                  disabled={loadingClients}
                  aria-label='Select client'
                >
                  <option value=''>
                    {loadingClients ? "Loading clients…" : "— Select client —"}
                  </option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} — {client.email}
                      {!client.isActive ? " (Inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {selectedId && (
            <>
              <div className='col-12'>
                <div
                  className='d-flex align-items-center gap-3 p-3 rounded-3'
                  style={{ background: "#faf5ff", border: "1px solid #e9d5ff" }}
                >
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "1rem",
                      flexShrink: 0,
                    }}
                  >
                    {selectedClient?.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className='fw-semibold' style={{ color: "#6b21a8" }}>
                      {selectedClient?.name}
                    </div>
                    <div className='small text-muted'>
                      {selectedClient?.email}
                      {selectedClient?.city ? ` • ${selectedClient.city}` : ""}
                      {!selectedClient?.isActive && (
                        <span className='ms-2 badge text-bg-secondary'>
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <PwField
                  id='clientNewPw'
                  label='New Password'
                  subtitle='Min. 8 characters — click ↻ to auto-generate'
                  value={newPw}
                  onChange={setNewPw}
                  show={showNew}
                  onToggleShow={() => setShowNew((v) => !v)}
                  borderColor='#a855f7'
                  bgColor='#f3e8ff'
                  iconColor='#7c3aed'
                  icon='bi-shield-lock-fill'
                  onGenerate={() => {
                    const p = generatePassword();
                    setNewPw(p);
                    setConfirmPw(p);
                  }}
                />
              </div>
              <div className='col-md-6'>
                <PwField
                  id='clientConfirmPw'
                  subtitle='Min. 8 characters — and should match the new password'
                  label='Confirm Password'
                  value={confirmPw}
                  onChange={setConfirmPw}
                  show={showConfirm}
                  onToggleShow={() => setShowConfirm((v) => !v)}
                  borderColor='#a855f7'
                  bgColor='#f3e8ff'
                  iconColor='#7c3aed'
                  icon='bi-shield-check'
                />
              </div>
              <div className='col-12'>
                <div
                  style={{ borderTop: "1.5px dashed #e2e8f0", paddingTop: 14 }}
                >
                  <button
                    type='submit'
                    disabled={isLoading}
                    style={{
                      background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                      border: "none",
                      color: "#fff",
                      borderRadius: 8,
                      padding: "7px 20px",
                      fontSize: "0.83rem",
                      fontWeight: 700,
                      cursor: isLoading ? "wait" : "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      boxShadow: "0 3px 10px #7c3aed33",
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        />{" "}
                        Resetting…
                      </>
                    ) : (
                      <>
                        <i className='bi bi-shield-check' /> Reset Client
                        Password
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ManagePasswordsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>("mine");

  const activeTabMeta = TABS.find((t) => t.id === activeTab)!;

  return (
    <section className='admin-page'>
      <div className='container-fluid px-0'>
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1.5px solid #e2e8f0",
            overflow: "hidden",
            boxShadow: "0 2px 12px #0000000a",
          }}
        >
          {/* Gradient header */}
          <div
            style={{
              background: "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)",
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className='bi bi-shield-lock-fill'
                style={{ color: "#fff", fontSize: 16 }}
              />
            </div>
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "#fff",
                  lineHeight: 1.2,
                }}
              >
                Manage Passwords
              </div>
              <div
                style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.8)" }}
              >
                Reset passwords for employees, clients, or change your own admin
                password
              </div>
            </div>
          </div>

          {/* Tab bar */}
          <div
            style={{
              display: "flex",
              borderBottom: "1.5px solid #e2e8f0",
              overflowX: "auto",
            }}
          >
            {TABS.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type='button'
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "12px 20px",
                    border: "none",
                    borderBottom: isActive
                      ? `3px solid ${tab.borderColor}`
                      : "3px solid transparent",
                    background: isActive
                      ? `${tab.accentColor}0d`
                      : "transparent",
                    color: isActive ? tab.accentColor : "#64748b",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.84rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition:
                      "color 0.15s, background 0.15s, border-color 0.15s",
                    flexShrink: 0,
                  }}
                >
                  <i className={`bi ${tab.icon}`} style={{ fontSize: 14 }} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Active tab subtitle strip */}
          <div
            style={{
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              padding: "8px 20px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <i
              className={`bi ${activeTabMeta.icon}`}
              style={{ color: activeTabMeta.accentColor, fontSize: 13 }}
            />
            <span
              style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 500 }}
            >
              {activeTab === "mine" &&
                "Change your own admin account password — current password required"}
              {activeTab === "employee" &&
                "Select an employee and set a new password on their behalf"}
              {activeTab === "client" &&
                "Select a client and reset their portal login password"}
            </span>
          </div>

          {/* Tab content */}
          <div className='p-4'>
            {activeTab === "mine" && <MyPasswordTab />}
            {activeTab === "employee" && <EmployeePasswordTab />}
            {activeTab === "client" && <ClientPasswordTab />}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ManagePasswordsPage;
